import { isDefined } from '@sgme/fp';
import type { Action } from 'state/actions';
import { addKey, addOrPatchKey, addOrPatchKeys } from 'state/utils/collection';
import { Collection } from 'state/utils/types';
import {
  initialSmartQuoteWorkSpaceState,
  SmartQuotePersistedTabContent,
  SmartQuotePrompt,
  SmartQuoteWorkspaceStateMap,
} from 'state/workspaces/smart-quote/model';

export function smartQuoteWorkspaceReducer(
  state: SmartQuoteWorkspaceStateMap = initialSmartQuoteWorkSpaceState,
  action: Action,
): SmartQuoteWorkspaceStateMap {
  switch (action.type) {
    case 'WORKSPACE_TAB_CREATED':
      if (action.workspace !== 'smartQuote') {
        return state;
      }
      return addOrPatchKey(state, action.id, () => ({
        tabId: action.id,
        prompts: {},
        promptsIds: [],
        isFetchingTabContent: false,
      }));

    case 'WORKSPACE_TAB_CONTENT_FETCH_REQUESTED':
      if (action.workspace !== 'smartQuote') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isFetchingTabContent: true }));

    case 'WORKSPACE_TAB_CONTENT_FETCHED':
      if (action.workspace !== 'smartQuote') {
        return state;
      }
      const tabContent = action.tabContent as SmartQuotePersistedTabContent;
      return addOrPatchKey(state, action.tabId, () => ({
        isFetchingTabContent: false,
        promptsIds: tabContent.promptsIds,
        prompts: {},
      }));

    case 'WORKSPACE_TAB_CONTENT_FETCH_FAILED':
      if (action.workspace !== 'smartQuote') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ prompts: {}, isFetchingTabContent: false }));

    case 'WORKSPACE_RESET_TAB_REQUESTED':
      if (action.workspace !== 'smartQuote') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        prompts: {},
        promptsIds: [],
        isFetchingTabContent: false,
      }));

    case 'PROMPTS_FETCHED':
      return addOrPatchKey(state, action.tabId, () => ({
        isFetchingTabContent: false,
        prompts: {
          ...state[action.tabId].prompts,
          ...action.prompts.reduce((acc, prompt) => ({ ...acc, [prompt.id]: prompt }), {}),
        },
      }));

    case 'PARSE_EMAIL_SUCCESS':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addKey<SmartQuotePrompt, Collection<SmartQuotePrompt>>(tabState.prompts, action.promptId, {
          id: action.promptId,
          createdAt: action.createdAt,
          filename: action.filename,
          selectedOnBehalfClient: action.selectedOnBehalfClient,
          status: 'Initialized',
          results: {},
        }),
      }));

    case 'ADD_RESULTS_TOTAL_COUNT':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, () => ({
          totalCount: action.totalCount,
          status: 'Processing' as const,
        })),
      }));

    case 'PARSING_START':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, promptState => ({
          results: addOrPatchKey(promptState.results, action.id, () => ({
            ...action,
            productKey: {
              instrument: { name: action.instrumentName, contractName: action.instrumentContractName },
              wrapper: action.wrapper,
            },
            status: 'StartParsing' as const,
          })),
        })),
      }));
    case 'PARSING_RESULT':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, promptState => ({
          results: addOrPatchKey(promptState.results, action.id, () => ({
            ...action,
            status: 'Parsed' as const,
          })),
        })),
      }));
    case 'PARSING_ERROR':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, promptState => ({
          results: addOrPatchKey(promptState.results, action.id, () => ({
            ...action,
            status: 'Error' as const,
          })),
        })),
      }));
    case 'QUOTE_RESPONSE':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, promptState => ({
          results: addOrPatchKey(promptState.results, action.id, () => ({
            ...action,
          })),
        })),
      }));

    case 'QUOTE_RECEIVED':
      if (action.workspace !== 'smartQuote') {
        return state;
      }
      const ids = Object.entries(state[action.tabId].prompts)
        .map(([id, prompt]) => {
          const resultEntry = Object.entries(prompt.results).find(([_, result]) => result.rfqId === action.quoteId);
          if (resultEntry) {
            return { promptId: id, resultId: resultEntry[0] };
          }
        })
        .find(isDefined);
      if (!ids) {
        return state;
      }
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, ids.promptId, promptState => ({
          results: addOrPatchKey(promptState.results, ids.resultId, () => ({
            externalQuoteId: action.quote.quoteExternalId,
            quoteStatus: action.quote.status,
            solveResult:
              action.quote.solveResultFormat === 'Percent' && action.quote.solveResult !== undefined
                ? action.quote.solveResult * 100
                : action.quote.solveResult,
            solveResultFormat: action.quote.solveResultFormat,
          })),
        })),
      }));

    case 'PARSING_FINISHED':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, () => ({
          status: 'Finished' as const,
        })),
      }));

    case 'ADD_CLIENT_CHECK_STEP':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, () => ({
          id: action.promptId,
          createdAt: new Date().toISOString(),
          filename: action.filename,
          status: 'Finished' as const,
          results: {},
          pendingFile: action.pendingFile,
        })),
      }));

    case 'UPDATE_IS_DISPLAYED_PRICE':
      return addOrPatchKey(state, action.tabId, tabState => ({
        prompts: addOrPatchKey(tabState.prompts, action.promptId, promptState => ({
          results: addOrPatchKeys(
            promptState.results,
            action.displayedPrices.map(price => {
              const resultWithIndex = Object.entries(promptState.results).find(
                ([_, result]) => result.id === price.resultId.toString(),
              )?.[0]!;

              return [
                resultWithIndex,
                {
                  ...promptState.results[resultWithIndex],
                  isDisplayedPrice: price.isDisplayedPrice,
                },
              ];
            }),
          ),
        })),
      }));

    default:
      return state;
  }
}
